import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { fontSize } from "@/components/signup/utils/font-size";
import {
    getProductData,
    ProductData,
    ProductKey
} from "@/components/signup/utils/products";
import { getSwitchAccountUrl } from "@/components/signup/utils/url";
import { useProductD0to6Site } from "@/components/user-context/helpers/cohorts";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";

export const ReturnToExistingProduct = ({
    exitView,
    productKeyForExpand
}: {
    exitView: () => void;
    productKeyForExpand: ProductKey;
}) => {
    const d0to6JiraSite = useProductD0to6Site({
        productKey: productKeyForExpand
    });
    const { product } = useUserContext();
    const {
        user: { email }
    } = useUserContext();
    const { productName } = getProductData(
        product?.key as keyof typeof ProductData
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const goToProductButtonText =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage({
                  id: "bxpSignup.siteExpansion.goToWorkspace",
                  defaultMessage: "Go to workspace"
              })
            : intl.formatMessage(
                  {
                      id: "bxpSignup.siteExpansion.goToProduct",
                      defaultMessage: "Go to {productName}"
                  },
                  {
                      productName
                  }
              );

    const createNewWorkspaceMessageValues = {
        link: (str: ReactNode) => (
            <Adg4Button css={createNewWorkspaceLinkCss} onClick={exitView}>
                {str}
            </Adg4Button>
        )
    };

    const createNewWorkspaceMessage =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedConfluenceCreateNewWorkspaceLink",
                      defaultMessage: "or <link>create a new workspace</link>"
                  },
                  createNewWorkspaceMessageValues
              )
            : intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink",
                      defaultMessage: "or <link>start a new site</link>"
                  },
                  createNewWorkspaceMessageValues
              );

    return !d0to6JiraSite ? null : (
        <div css={containerCss}>
            <div css={contentCss}>
                <p css={switchAccountSubheader}>
                    <FormattedMessage
                        id="BxpSignup.SiteExpansionForm.cobrandedSubheader.2"
                        defaultMessage="You’re logged in as {email}. <switchAccountLink>Switch account</switchAccountLink>"
                        values={{
                            email: <strong css={emailCss}>{email}</strong>,
                            switchAccountLink
                        }}
                    />
                </p>
                <div
                    css={boxContainerCss}
                    data-testid="return-to-existing-product"
                >
                    <a
                        css={siteBoxCss}
                        href={getProductUrl({
                            url: d0to6JiraSite.url,
                            productKey: product?.key ?? ""
                        })}
                        onClick={(e) => {
                            e.preventDefault(); // wait for event to fire
                            createAnalyticsEvent({
                                eventType: "ui",
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId:
                                    "contactExpandClickReturnToProductButton",
                                attributes: {
                                    productName
                                }
                            }).fire();
                            window.location.href = getProductUrl({
                                url: d0to6JiraSite.url,
                                productKey: product?.key ?? ""
                            });
                        }}
                    >
                        <div css={siteCss}>
                            <p css={bold}>{productName}</p>
                            <div css={siteNameCss}>
                                {d0to6JiraSite.displayName}
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            size="small"
                            color="blue"
                            css={goToProductButtonCss}
                        >
                            {goToProductButtonText}
                        </Button>
                    </a>
                </div>
            </div>
            <div css={createNewWorkspaceMessageCss}>
                {createNewWorkspaceMessage}
            </div>
        </div>
    );
};

const switchAccountLink = (children: ReactNode) => (
    <a href={getSwitchAccountUrl()} css={switchAccountLinkCss}>
        {children}
    </a>
);
const getProductUrl = ({
    url,
    productKey
}: {
    url: string;
    productKey: string;
}) => {
    switch (productKey) {
        case ProductKey.JIRA_SOFTWARE:
            return `${url}/jira`;
        case ProductKey.CONFLUENCE:
            return `${url}/wiki`;
        default:
            return url;
    }
};

const switchAccountSubheader = css({
    fontFamily: "inherit",
    fontSize: 14,
    margin: 0,
    textAlign: "center"
});

const containerCss = css({
    alignContent: "space-between",
    display: "grid",
    flexGrow: 1,
    gap: 16
});

const contentCss = css({
    display: "grid",
    gap: 32
});

const createNewWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});

const createNewWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const boxContainerCss = css({
    alignItems: "center",
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    borderRadius: 8,
    display: "grid",
    padding: 16,
    width: "100%"
});

const siteBoxCss = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    [":hover"]: {
        textDecoration: "none !important"
    }
});

const bold = css({
    fontWeight: "400",
    margin: 0
});

const siteCss = css({
    fontSize: fontSize.md,
    overflow: "hidden"
});

const siteNameCss = css({
    color: wacToken("color.neutral.light700"),
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const emailCss = css({
    fontWeight: 600,
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    wordWrap: "break-word"
});

const switchAccountLinkCss = css({
    color: wacToken("color.primary.blue"),
    textDecoration: "underline",
    whiteSpace: "nowrap",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const goToProductButtonCss = css({
    fontFamily: "inherit",
    fontSize: 16,
    paddingBlock: 8
});
